import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Fab,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AuthModal from "./AuthModal";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

export default function NavBar() {
  const location = useLocation();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Listen for auth changes and fetch user role
  useEffect(() => {
    const getSessionAndRole = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      if (data.session) {
        const { data: userData } = await supabase
          .from("users")
          .select("role")
          .eq("id", data.session.user.id)
          .single();
        setUserRole(userData?.role);
      } else {
        setUserRole(null);
      }
    };
    getSessionAndRole();
    
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        supabase
          .from("users")
          .select("role")
          .eq("id", session.user.id)
          .single()
          .then(({ data }) => setUserRole(data?.role));
        setAuthModalOpen(false);
      } else {
        setUserRole(null);
      }
    });
    
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUserRole(null);
  };

  // Helper for active button style
  const isActive = (path) => location.pathname === path;

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: "rgba(20, 20, 30, 0.7)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          borderBottom: "3px solid #f49330",
          zIndex: 1201,
        }}
        elevation={0}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: 64,
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left section - Logo and Leaderboard (desktop only) */}
            <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 180 }}>
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                    mr: 22,
                    height: 108,
                  }}
                >
                  <img
                    src="https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/logos/MV_Orange_Logo.webp"
                    alt="Manga Versus Logo"
                    style={{
                      height: 130,
                      width: "auto",
                      display: "block",
                    }}
                  />
                </Box>
              </Box>

              {/* Left of center: Leaderboard */}
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 170, justifyContent: "flex-end" }}>
                <Button
                  component={Link}
                  to="/leaderboard"
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: isActive("/leaderboard") ? "#f49330" : "#fff",
                    border: isActive("/leaderboard") ? "2px solid #f49330" : "2px solid transparent",
                    borderRadius: 2,
                    px: 4,
                    py: 1,
                    background: isActive("/leaderboard") ? "rgba(244,147,48,0.08)" : "transparent",
                    "&:hover": {
                      color: "#fff176",
                      border: "2px solid #fff176",
                      background: "rgba(244,147,48,0.12)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  Rankings
                </Button>
              </Box>
            </Box>

            {/* Center: VERSUS Button - Absolutely positioned for perfect centering (desktop only) */}
            <Box
              sx={{
                display: { xs: "none", lg: "block" }, // Hide on mobile and tablets
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)", // Perfect center positioning
                zIndex: 2, // Ensure it's above other elements
              }}
            >
              <Button
                component={Link}
                to="/versus-lobby"
                variant="contained"
                sx={{
                  background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                  color: "#181818",
                  fontWeight: 700,
                  fontSize: 30,
                  px: 8,
                  py: 2,
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                    boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                  minWidth: 140,
                }}
              >
                VERSUS
              </Button>
            </Box>

            {/* Right section - Character List, Login, Discord (desktop only) */}
            <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", mr: -10 }}>
              {/* Character List Button */}
              <Button
                component={Link}
                to="/character-list"
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: isActive("/character-list") ? "#f49330" : "#fff",
                  border: isActive("/character-list") ? "2px solid #f49330" : "2px solid transparent",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  background: isActive("/character-list") ? "rgba(244,147,48,0.08)" : "transparent",
                  "&:hover": {
                    color: "#fff176",
                    border: "2px solid #fff176",
                    background: "rgba(244,147,48,0.12)",
                  },
                  transition: "all 0.2s",
                  mr: 10, // Increased spacing between Character List and Login button
                }}
              >
                CHARACTERS
              </Button>
              
              {/* Login/Profile Button with Logout */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  color="inherit"
                  variant="contained"
                  onClick={() => session ? null : setAuthModalOpen(true)}
                  component={session ? Link : "button"}
                  to={session ? "/profile" : undefined}
                  sx={{
                    borderColor: "#f49330",
                    background: "#f49330",
                    color: "#181818",
                    fontWeight: 700,
                    borderRadius: session ? "8px 0 0 8px" : 2, // Rounded left corners only when logged in
                    minWidth: { xs: 120, sm: 150 },
                    fontSize: { xs: 16, sm: 20 },
                    px: { xs: 1, sm: 3 },
                    py: 1,
                    boxShadow: "none",
                    "&:hover": {
                      background: "#fff176",
                      color: "#181818",
                      borderColor: "#fff176",
                    },
                    whiteSpace: "nowrap",
                    mr: session ? 0 : 5, // No right margin when logged in (logout button will handle spacing)
                  }}
                >
                  {session ? "PROFILE" : "LOGIN / SIGNUP"}
                </Button>
                
                {/* Logout Button - Only shown when logged in */}
                {session && (
                  <Button
                    onClick={handleLogout}
                    sx={{
                      borderColor: "#d32f2f",
                      background: "#d32f2f",
                      color: "#fff",
                      fontWeight: 700,
                      borderRadius: "0 8px 8px 0", // Rounded right corners only
                      minWidth: 48,
                      maxWidth: 48,
                      fontSize: { xs: 16, sm: 20 }, // Match profile button's responsive font size
                      px: 1,
                      py: 1, // Match the profile button's py: 1
                      minHeight: 51, // Match the profile button's full height
                      boxShadow: "none",
                      "&:hover": {
                        background: "#b71c1c",
                        color: "#fff",
                        borderColor: "#b71c1c",
                      },
                      whiteSpace: "nowrap",
                      mr: 5, // Spacing between logout button and discord button
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LogoutIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> {/* Responsive icon size */}
                  </Button>
                )}
              </Box>
              
              {/* Discord Button */}
              <IconButton
                component="a"
                href="https://discord.gg/6XChzYYYrr"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "#5865F2",
                  color: "#fff",
                  boxShadow: "0 2px 8px #0004",
                  "&:hover": {
                    background: "#4752C4",
                  },
                }}
              >
                <svg width="24" height="24" viewBox="0 0 127.14 96.36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#fff" d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                </svg>
              </IconButton>
            </Box>

            {/* Hamburger menu icon for mobile/tablet */}
            <Box sx={{ display: { xs: "flex", lg: "none" }, ml: 1 }}>
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Centered logo for mobile/tablet */}
            <Box
              sx={{
                display: { xs: "flex", lg: "none" },
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 2,
                alignItems: "center",
                height: 64,
              }}
            >
              <Box
                component={Link}
                to="/"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  height: 48,
                }}
              >
                <img
                  src="https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/logos/MV_Orange_Logo.webp"
                  alt="Manga Versus Logo"
                  style={{
                    height: 80,
                    width: "auto",
                    display: "block",
                  }}
                />
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Drawer for mobile/tablet navigation */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 240,
            maxHeight: "90vh",
            height: "auto",
            top: 0,
            left: 0,
            borderRadius: "0 12px 12px 0",
            // --- MATCH NAVBAR VISUAL STYLE ---
            background: "rgba(20, 20, 30, 0.7)", // Match AppBar
            backdropFilter: "blur(12px)",        // Match AppBar
            WebkitBackdropFilter: "blur(12px)",  // Match AppBar
            border: "2px solid #f49330",
            boxShadow: "0 4px 24px rgba(244,147,48,0.18)",
            color: "#fff",
            p: 0,
          }
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 240,
            maxHeight: "90vh",
            height: "auto",
            borderRadius: "0 12px 12px 0",
            background: "rgba(20, 20, 30, 0.7)", // Match AppBar
            backdropFilter: "blur(12px)",        // Match AppBar
            WebkitBackdropFilter: "blur(12px)",  // Match AppBar
            border: "2px solid #f49330",
            boxShadow: "0 4px 24px rgba(244,147,48,0.18)",
            color: "#fff",
            p: 0,
          },
        }}
      >
        <List sx={{ p: 0 }}>
          {[
            { label: "Home", to: "/" },
            { label: "Rankings", to: "/leaderboard" },
            { label: "Characters", to: "/character-list" },
            { label: "Versus", to: "/versus-lobby" },
          ].map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                component={Link}
                to={item.to}
                onClick={handleDrawerToggle}
                sx={{
                  px: 3,
                  py: 2,
                  fontWeight: 700,
                  fontSize: 18,
                  color: location.pathname === item.to ? "#f49330" : "#fff",
                  background: location.pathname === item.to ? "rgba(244,147,48,0.08)" : "transparent",
                  borderLeft: location.pathname === item.to ? "4px solid #f49330" : "4px solid transparent",
                  "&:hover": {
                    color: "#fff176",
                    background: "rgba(244,147,48,0.12)",
                    borderLeft: "4px solid #fff176",
                  },
                  transition: "all 0.2s",
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
          {session ? (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/profile"
                onClick={handleDrawerToggle}
                sx={{
                  px: 3,
                  py: 2,
                  fontWeight: 700,
                  fontSize: 18,
                  color: location.pathname === "/profile" ? "#f49330" : "#fff",
                  background: location.pathname === "/profile" ? "rgba(244,147,48,0.08)" : "transparent",
                  borderLeft: location.pathname === "/profile" ? "4px solid #f49330" : "4px solid transparent",
                  "&:hover": {
                    color: "#fff176",
                    background: "rgba(244,147,48,0.12)",
                    borderLeft: "4px solid #fff176",
                  },
                  transition: "all 0.2s",
                }}
              >
                <ListItemText primary="Profile" />
              </ListItemButton>
            </ListItem>
          ) : (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => { setAuthModalOpen(true); handleDrawerToggle(); }}
                sx={{
                  px: 3,
                  py: 2,
                  fontWeight: 700,
                  fontSize: 18,
                  color: "#fff",
                  "&:hover": {
                    color: "#fff176",
                    background: "rgba(244,147,48,0.12)",
                    borderLeft: "4px solid #fff176",
                  },
                  transition: "all 0.2s",
                }}
              >
                <ListItemText primary="Login / Signup" />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton
              component="a"
              href="https://discord.gg/6XChzYYYrr"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDrawerToggle}
              sx={{
                px: 3,
                py: 2,
                fontWeight: 700,
                fontSize: 18,
                color: "#fff",
                "&:hover": {
                  color: "#fff176",
                  background: "rgba(244,147,48,0.12)",
                  borderLeft: "4px solid #fff176",
                },
                transition: "all 0.2s",
              }}
            >
              <ListItemText primary="Discord" />
            </ListItemButton>
          </ListItem>
          {session && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => { handleLogout(); handleDrawerToggle(); }}
                sx={{
                  px: 3,
                  py: 2,
                  fontWeight: 700,
                  fontSize: 18,
                  color: "#fff",
                  "&:hover": {
                    color: "#fff176",
                    background: "rgba(244,147,48,0.12)",
                    borderLeft: "4px solid #fff176",
                  },
                  transition: "all 0.2s",
                }}
              >
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>

      {/* Admin Floating Action Button */}
      {session && userRole === "admin" && (
        <Fab
          component={Link}
          to="/character-manager"
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
            color: "#181818",
            zIndex: 1000,
            "&:hover": {
              background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
              transform: "scale(1.05)",
            },
            transition: "all 0.3s ease",
            boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
          }}
        >
          <AdminPanelSettingsIcon sx={{ fontSize: 28 }} />
        </Fab>
      )}

      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
}